/* eslint-disable react/destructuring-assignment */
import { Flex, Input, Text } from "@chakra-ui/react";
import styled from "@emotion/styled";
import { omit } from "lodash/fp";
import moment from "moment";
import {
  CSSProperties,
  ChangeEvent,
  ChangeEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { INPUT_STYLES } from "design_system/components/OFInput/styles";
import { colors } from "src/colors";
import { DATE_FORMAT } from "src/constants/Common";
import { TEXT_STYLES } from "src/typography";

const StyledFlex = styled(Flex)`
  border-color: ${colors.grey[900]};

  &:hover {
    border-color: ${colors.black[900]};
  }
`;

type DateInfo = {
  day: string;
  month: string;
  year: string;
};

const convertStringDate = (value: string): DateInfo => {
  return {
    day: moment(value).date().toString().padStart(2, "0"),
    month: (moment(value).month() + 1).toString().padStart(2, "0"),
    // for some reason, in JP, moment(value, DATE_FORMAT).format('yyyy') return wrong value
    // ex: 1990, that function returns 0002
    year: moment(value).year().toString()
  };
};

interface InputDateProps {
  value?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
  style?: CSSProperties;
}

// THIS IS OBSOLETED, PLEASE USE OFDateInput instead
export const InputDate = (props: InputDateProps) => {
  const textStyles = {
    fontFamily: "graphik",
    ...omit(["as"], TEXT_STYLES.body3)
  };

  const wrapperStyles = omit(["_groupHover"], INPUT_STYLES);

  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  const value = props?.value || "";

  const isDateValid = useMemo(() => moment(value, DATE_FORMAT).isValid(), [value]);
  const [dateValue, setDateValue] = useState(
    isDateValid
      ? convertStringDate(value)
      : {
          day: "",
          month: "",
          year: ""
        }
  );

  useEffect(() => {
    if (isDateValid) {
      setDateValue({
        ...convertStringDate(value)
      });
    }
  }, [isDateValid, value]);

  const onChangeDateValue = (
    key: "day" | "month" | "year",
    inputValue: ChangeEvent<HTMLInputElement>
  ) => {
    setDateValue({
      ...dateValue,
      [key]: inputValue.target.value
    });
    let isDateInputReady = false;
    let date = "";
    // eslint-disable-next-line default-case
    switch (key) {
      case "day":
        if (
          dateValue.month.length === 2 &&
          dateValue.year.length === 4 &&
          inputValue.target.value.length === 2
        ) {
          isDateInputReady = true;
          date = `${dateValue.year}-${dateValue.month}-${inputValue.target.value}`;
        }
        if (inputValue.target.value.length === 2) {
          monthRef.current.focus();
        }
        break;
      case "month":
        if (
          dateValue.day.length === 2 &&
          dateValue.year.length === 4 &&
          inputValue.target.value.length === 2
        ) {
          isDateInputReady = true;
          date = `${dateValue.year}-${inputValue.target.value}-${dateValue.day}`;
        }
        if (inputValue.target.value.length === 2) {
          yearRef.current.focus();
        }
        break;
      case "year":
        if (
          dateValue.day.length === 2 &&
          dateValue.month.length === 2 &&
          inputValue.target.value.length === 4
        ) {
          isDateInputReady = true;
          date = `${inputValue.target.value}-${dateValue.month}-${dateValue.day}`;
        }
        break;
    }

    if (isDateInputReady && moment(date, DATE_FORMAT, true).isValid()) {
      inputValue.target.value = date;
      props.onChange(inputValue);
    } else {
      inputValue.target.value = null;
      props.onChange(inputValue);
    }
  };

  return (
    <StyledFlex {...wrapperStyles} p="14px 12px" w="100%" role="group" style={props.style}>
      <Input
        {...textStyles}
        value={dateValue.day}
        onChange={(e) => {
          if (!Number.isNaN(Number(e.target.value))) {
            onChangeDateValue("day", e);
          }
        }}
        textAlign="start"
        maxLength={2}
        variant="unstyled"
        placeholder="DD"
        width="30px"
        disabled={props.disabled}
        ref={dayRef}
      />
      <Text {...textStyles} opacity={dateValue.day.length < 2 ? 0.4 : 1}>
        /
      </Text>
      <Input
        {...textStyles}
        value={dateValue.month}
        onChange={(e) => {
          if (!Number.isNaN(Number(e.target.value))) {
            onChangeDateValue("month", e);
          }
        }}
        textAlign="start"
        width="30px"
        maxLength={2}
        variant="unstyled"
        marginLeft="5px"
        placeholder="MM"
        disabled={props.disabled}
        ref={monthRef}
      />
      <Text {...textStyles} opacity={dateValue.month.length < 2 ? 0.4 : 1}>
        /
      </Text>
      <Input
        {...textStyles}
        value={dateValue.year}
        onChange={(e) => {
          if (!Number.isNaN(Number(e.target.value))) {
            onChangeDateValue("year", e);
          }
        }}
        textAlign="start"
        maxLength={4}
        width="42px"
        variant="unstyled"
        placeholder="YYYY"
        marginLeft="5px"
        disabled={props.disabled}
        ref={yearRef}
      />
    </StyledFlex>
  );
};
